export type RenderDrawPath = "immediate" | "scheduled";

export type PrepareCacheKind =
  | "camera-reuse"
  | "signature-cache"
  | "preview-promotion"
  | "retained-transform"
  | "retained-incremental"
  | "fresh";

export interface RenderTelemetryFrame {
  trigger: string;
  drawPath: RenderDrawPath;
  renderMode: "full" | "camera";
  prepareCache: PrepareCacheKind;
  controllerWaitMs: number;
  rendererWaitMs: number;
  buildOverlayMs: number;
  tessellationMs: number;
  previewMs: number;
  classifyMs: number;
  composeTilesMs: number;
  composeLiveMs: number;
  composeBackdropMs: number;
  retessellatedNodeCount?: number;
  geometryPoolHits?: number;
}

const emptyFrame = (): RenderTelemetryFrame => ({
  trigger: "unknown",
  drawPath: "scheduled",
  renderMode: "full",
  prepareCache: "fresh",
  controllerWaitMs: 0,
  rendererWaitMs: 0,
  buildOverlayMs: 0,
  tessellationMs: 0,
  previewMs: 0,
  classifyMs: 0,
  composeTilesMs: 0,
  composeLiveMs: 0,
  composeBackdropMs: 0,
});

let active = emptyFrame();
let requestScheduledAt = 0;
let rendererScheduledAt = 0;

export function resetRenderTelemetry(): void {
  const scheduled = active;
  active = {
    ...emptyFrame(),
    trigger: scheduled.trigger,
    drawPath: scheduled.drawPath,
    controllerWaitMs: scheduled.controllerWaitMs,
    rendererWaitMs: scheduled.rendererWaitMs,
    buildOverlayMs: scheduled.buildOverlayMs,
  };
}

export function getRenderTelemetry(): RenderTelemetryFrame {
  return active;
}

export function setRenderTelemetryTrigger(trigger: string): void {
  active.trigger = trigger;
}

export function markRenderRequestScheduled(trigger = "unknown"): void {
  requestScheduledAt = performance.now();
  active.trigger = trigger;
}

export function markRendererDrawScheduled(): void {
  rendererScheduledAt = performance.now();
  if (requestScheduledAt > 0) {
    active.controllerWaitMs = Math.max(0, rendererScheduledAt - requestScheduledAt);
  }
}

export function patchRenderTelemetry(patch: Partial<RenderTelemetryFrame>): void {
  active = { ...active, ...patch };
}

export function recordBuildOverlayMs(ms: number): void {
  active.buildOverlayMs = ms;
}

export function recordControllerFlushStart(): void {
  if (requestScheduledAt > 0) {
    active.controllerWaitMs = Math.max(0, performance.now() - requestScheduledAt);
  }
}

export function recordRendererDrawStart(drawPath: RenderDrawPath): void {
  active.drawPath = drawPath;
  if (drawPath === "scheduled" && rendererScheduledAt > 0) {
    active.rendererWaitMs = Math.max(0, performance.now() - rendererScheduledAt);
  } else {
    active.rendererWaitMs = 0;
  }
  requestScheduledAt = 0;
  rendererScheduledAt = 0;
}
